/**
 * 汉字笔画数计算工具 - 使用 cnchar 库
 * 支持任意汉字的笔画数计算,无需维护映射表
 * 支持简体字和繁体字
 */

import cnchar from 'cnchar';
import trad from 'cnchar-trad';

// 加载繁体字支持插件
cnchar.use(trad);

// 笔画数分类范围
const STROKE_GROUPS = [
  { label: "一畫", range: [1, 1] },
  { label: "二畫", range: [2, 2] },
  { label: "三畫", range: [3, 3] },
  { label: "四畫", range: [4, 4] },
  { label: "五畫", range: [5, 5] },
  { label: "六畫", range: [6, 6] },
  { label: "七畫", range: [7, 7] },
  { label: "八畫", range: [8, 8] },
  { label: "九畫", range: [9, 9] },
  { label: "十畫", range: [10, 10] },
  { label: "十一畫", range: [11, 11] },
  { label: "十二畫", range: [12, 12] },
  { label: "十三畫", range: [13, 13] },
  { label: "十四畫", range: [14, 14] },
  { label: "十五畫", range: [15, 15] },
  { label: "十六畫", range: [16, 16] },
  { label: "十七畫", range: [17, 17] },
  { label: "十八畫", range: [18, 18] },
  { label: "十九畫", range: [19, 19] },
  { label: "二十畫", range: [20, 20] },
  { label: "二十一畫", range: [21, 21] },
  { label: "二十二畫", range: [22, 22] },
  { label: "二十三畫", range: [23, 23] },
  { label: "二十四畫", range: [24, 24] },
  { label: "二十五畫", range: [25, 25] },
  { label: "二十六畫", range: [26, 26] },
  { label: "二十七畫", range: [27, 27] },
  { label: "二十八畫", range: [28, 28] },
  { label: "二十九畫", range: [29, 29] },
  { label: "三十畫及以上", range: [30, 99] },
];

/**
 * 获取姓氏的总笔画数
 * 如果姓氏有多个字,将所有字的笔画数相加
 *
 * @param chineseSurname - 中文姓氏(可以是单字或多字)
 * @returns 总笔画数
 *
 * @example
 * getSurnameStrokeCount('李') // 7
 * getSurnameStrokeCount('欧阳') // 15 + 17 = 32
 * getSurnameStrokeCount('司马') // 5 + 10 = 15
 */
export const getSurnameStrokeCount = (chineseSurname: string): number => {
  if (!chineseSurname) return 0;

  let totalStrokes = 0;

  // 遍历姓氏中的每个字,累加笔画数
  for (let i = 0; i < chineseSurname.length; i++) {
    const char = chineseSurname.charAt(i);
    // 使用 cnchar 获取单个汉字的笔画数
    const strokes = cnchar.stroke(char);

    // cnchar.stroke 返回数字或字符串,需要转换为数字
    const strokeCount = typeof strokes === 'number' ? strokes : parseInt(strokes) || 0;
    totalStrokes += strokeCount;
  }

  return totalStrokes;
};

/**
 * 根据笔画数获取分组标签
 */
export const getStrokeGroupLabel = (strokeCount: number): string => {
  // 笔画数为 0 或无效时返回空字符串,这样的客户将被过滤掉
  if (strokeCount === 0) return "";

  const group = STROKE_GROUPS.find(
    (g) => strokeCount >= g.range[0] && strokeCount <= g.range[1]
  );

  // 找不到匹配的分组时返回空字符串
  return group ? group.label : "";
};

/**
 * 获取所有笔画分组
 */
export const getAllStrokeGroups = () => {
  // 只返回预定义的笔画分组,不包含"其他"
  return STROKE_GROUPS;
};

/**
 * 从客户数据中提取姓氏
 * 注意: 这里不再提取首字,而是返回完整的 chineseSurname 字段
 *
 * @param chineseSurname - 中文姓氏字段
 * @returns 完整姓氏
 */
export const extractSurname = (chineseSurname?: string): string => {
  return chineseSurname || "";
};
